/**
 * B2B partner video — soundtrack options.
 *
 * The composition takes a `music` prop; the beat grid, every scene cut and
 * the total duration follow the chosen track's tempo (see `timing.ts`), so
 * swapping tracks in the Studio props panel keeps the cuts on the beat.
 *
 * Mixkit tracks are under the Mixkit Stock Music Free License: commercial
 * use in online videos, social media and online ads, no attribution
 * required; no TV / radio broadcast, no resale or redistribution as music.
 * https://mixkit.co/license/#musicFree
 *
 * Tempo and beat phase measured with an onset-envelope comb (product-film
 * skill, `scripts/beats.py`; all sit on a clean grid, ±3 ms). `startBeat`
 * skips into the track so its energy lift lands on the "Frak transforme…"
 * reveal cut (video beat 20): the pain scenes ride the quieter part, the
 * answer gets the full band. After that Motivating Mornings is flat
 * (per-bar RMS: +1 dB at the pricing cut, beat 56; its loudest bar starts
 * on video beat 80, four beats into the CTA), so `B2BVideo` lifts the bed
 * itself on the pricing and CTA cuts.
 */

export type Track = {
  label: string;
  file: string;
  /** First 30s through a low-pass (`scripts/b2b-audio.sh`): the muffled intro. */
  muffled: string;
  bpm: number;
  /**
   * Where the beat grid sits in the file, in seconds: beat n plays at
   * `beatPhase + n * 60 / bpm` (negative = the grid starts just before
   * the file). The trim uses it so the music's beats land on the cuts.
   */
  beatPhase: number;
  /** Track beat that plays at frame 0 (always a whole bar here). */
  startBeat: number;
  /**
   * Linear gain, levelled so every track alone renders at ~-18 LUFS.
   * `B2BVideo` then plays it as a softer bed (`MUSIC_BED`) under the SFX.
   */
  volume: number;
  source: string;
};

export const TRACKS = {
  /**
   * Corporate, bright piano/guitar/drums. Quiet 12s intro, the band comes in
   * on beat 24 (after a one-beat dip); startBeat 4 puts that on the reveal.
   */
  motivatingMornings: {
    label: "Motivating Mornings — Ahjay Stelino (Mixkit)",
    file: "b2b/music/motivating-mornings.mp3",
    muffled: "b2b/music/motivating-mornings.muffled.mp3",
    bpm: 121,
    beatPhase: -0.01,
    startBeat: 4,
    volume: 0.43,
    source: "https://mixkit.co/free-stock-music/ (id 33)",
  },
  /**
   * Tech house, steady and modern. Lighter intro, full groove from beat 32;
   * startBeat 12 puts it on the reveal.
   */
  techHouseVibes: {
    label: "Tech House Vibes — Alejandro Magaña (Mixkit)",
    file: "b2b/music/tech-house-vibes.mp3",
    muffled: "b2b/music/tech-house-vibes.muffled.mp3",
    bpm: 122,
    beatPhase: 0.039,
    startBeat: 12,
    volume: 0.38,
    source: "https://mixkit.co/free-stock-music/ (id 130)",
  },
  /** House, even energy from the first beat; breakdown only after 46s. */
  talentInTheAir: {
    label: "Talent in the Air — Diego Nava (Mixkit)",
    file: "b2b/music/talent-in-the-air.mp3",
    muffled: "b2b/music/talent-in-the-air.muffled.mp3",
    bpm: 122,
    beatPhase: -0.012,
    startBeat: 0,
    volume: 0.52,
    source: "https://mixkit.co/free-stock-music/ (id 473)",
  },
  /** The B2C video's song, kept for comparison (the original B2B cut). */
  b2cSong: {
    label: "B2C song (original B2B cut)",
    file: "song.mp3",
    muffled: "b2b/music/b2c-song.muffled.mp3",
    // Measured (see `b2c/timing.ts`); 1800/14 was the old whole-frame beat.
    bpm: 129.33,
    beatPhase: 0.029,
    startBeat: 0,
    volume: 0.3,
    source: "public/song.mp3",
  },
} satisfies Record<string, Track>;

export type MusicKey = keyof typeof TRACKS;

export const MUSIC_KEYS = Object.keys(TRACKS) as MusicKey[];

/** Soundtrack used when the `music` prop is not set. */
export const DEFAULT_MUSIC: MusicKey = "motivatingMornings";
